import type { FC } from 'react'

import { Translation } from '@/app/translations/Translation'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDateTime } from '@/components/LocalDateTime'
import RouterLink from '@/components/RouterLink'
import type { AssetExtended } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

export const EcomPolicyOrigin: FC<{ assetDetails: AssetExtended }> = ({ assetDetails }) => {
  const {
    ecomOrderTotalAmount,
    ecomOrderCoverAmount,
    ecomOrderRemainderAmount,
    ecomOrderCurrency,
    ecomOrderDate,
    ecomOrderId,
    isMigratedIntoEcom,
  } = assetDetails

  const coverAmountPercentage =
    ecomOrderCoverAmount && ecomOrderTotalAmount
      ? Math.round((ecomOrderCoverAmount / ecomOrderTotalAmount) * 100)
      : 0

  const remainderAmountPercentage =
    ecomOrderRemainderAmount && ecomOrderTotalAmount
      ? Math.round((ecomOrderRemainderAmount / ecomOrderTotalAmount) * 100)
      : 0

  if (!ecomOrderId || isMigratedIntoEcom) {
    return null
  }

  return (
    <section className="shadow-basic m-2 flex flex-1 basis-[30rem] rounded-md bg-white p-4">
      <DataPoints>
        <h3>
          <Translation id="Order Details" />
        </h3>
        <DataPoint
          header={<Translation id="Order Id" />}
          value={
            <RouterLink
              to={APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(
                ':ecomPolicyOrderId',
                ecomOrderId.toString()
              )}
              hasUnderline={false}
            >
              <div className="text-lg">{ecomOrderId}</div>
            </RouterLink>
          }
        />

        <DataPoint
          header={<Translation id="Ordered" />}
          value={<LocalDateTime date={ecomOrderDate} />}
        />

        <DataPoint
          header={<Translation id="Covered Amount" />}
          value={
            <>
              <FormattedNumberWithCurrency
                amount={ecomOrderCoverAmount}
                currency={ecomOrderCurrency}
              />
              {ecomOrderCoverAmount !== 0 && (
                <span className="ml-1.5 align-middle text-xs text-black opacity-50">
                  {coverAmountPercentage}%
                </span>
              )}
            </>
          }
        />

        <DataPoint
          header={<Translation id="Deductable Amount" />}
          value={
            <>
              <FormattedNumberWithCurrency
                amount={ecomOrderRemainderAmount}
                currency={ecomOrderCurrency}
              />
              {ecomOrderRemainderAmount !== 0 && (
                <span className="ml-1.5 align-middle text-xs text-black opacity-50">
                  {remainderAmountPercentage}%
                </span>
              )}
            </>
          }
        />
        <DataPoint
          header={<Translation id="Total Cost" />}
          value={
            <FormattedNumberWithCurrency
              amount={ecomOrderTotalAmount}
              currency={ecomOrderCurrency}
            />
          }
        />
      </DataPoints>
    </section>
  )
}
